import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "../assets/costOfWorks.module.css";

const CostOfWorks = () => {
  const { id: clientIdFromUrl } = useParams(); // Get client ID from URL if available
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    date_type: "",
    date: "",
    date_to: "",
    status_id: "All",
    property_type: "", // Empty string for "Active only" (default), "all" for "All"
  });
  const [statuses, setStatuses] = useState([]); // Dynamic statuses from backend
  const [searchTrigger, setSearchTrigger] = useState(0); // Trigger for search

  // Date type options matching Laravel (which date field to filter on)
  const dateTypes = {
    "": "Select date",
    submitted: "Submitted",
    deadline: "Deadline",
    completed: "Completed"
  };

  const loadTableData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      console.log("Loading cost of works data with DataTable format...");

      // Use client-specific endpoint if clientId is in URL, otherwise use general endpoint
      const apiUrl = clientIdFromUrl 
        ? `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/tickets/clients/${clientIdFromUrl}/cost-of-works`
        : `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/tickets/client/cost-of-works`;

      // Prepare DataTable parameters matching Laravel format
      const requestData = {
        draw: currentPage,
        start: (currentPage - 1) * pageSize,
        length: pageSize,
        // Search/filter parameters
        status_id: filters.status_id,
        property_type: filters.property_type,
        date_type: filters.date_type,
        date: filters.date,
        date_to: filters.date_to,
      };

      console.log("Cost of works request data:", requestData);

      const response = await fetch(apiUrl, {
        method: "POST", // Use POST for DataTable requests like Laravel
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      console.log("Cost of works response status:", response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Failed to fetch cost data: ${response.status} ${response.statusText} - ${errorData.message || "Unknown error"}`
        );
      }

      const data = await response.json();
      console.log("Cost of works DataTable response:", data);

      // Handle Laravel DataTable response format
      if (data.data) {
        setTableData(data.data);
        setTotalRecords(data.recordsTotal || 0);
        setTotalPages(Math.ceil((data.recordsTotal || 0) / pageSize));
      } else {
        setTableData([]);
        setTotalRecords(0);
        setTotalPages(1);
      }

    } catch (err) {
      console.error("Cost of works error:", err);
      setError(err.message || "Failed to load cost data");
      toast.error("Failed to load cost of works data");

      // Set empty data on error
      setTableData([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTrigger, clientIdFromUrl]);

  // Fetch available statuses on component mount
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/tickets/filter-data`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.statuses) {
            setStatuses(data.statuses);
          }
        }
      } catch (error) {
        console.error("Error fetching statuses:", error);
      }
    };
    fetchStatuses();
  }, []);

  useEffect(() => {
    loadTableData();
  }, [loadTableData]);

  const handleSearch = async (e) => {
    e.preventDefault();
    console.log("Searching cost of works with filters:", filters);
    setCurrentPage(1); // Reset to first page
    setSearchTrigger(prev => prev + 1); // Trigger search
  };

  const handleFilterChange = (field, value) => {
    // Convert HTML5 date input (YYYY-MM-DD) to Laravel format (YYYY-MM-DD for API)
    let processedValue = value;
    if ((field === 'date' || field === 'date_to') && value) {
      // HTML5 date inputs already return YYYY-MM-DD format which is what our API expects
      processedValue = value;
    }
    
    setFilters(prev => ({
      ...prev,
      [field]: processedValue,
    }));
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const exportCSV = async () => {
    console.log("Exporting CSV...");
    try {
      const token = localStorage.getItem("token");
      
      // Build query string from filters (include all filters like the main query)
      const queryParams = new URLSearchParams();
      if (filters.status_id && filters.status_id !== "All") {
        queryParams.append("status_id", filters.status_id);
      }
      if (filters.property_type !== undefined) {
        queryParams.append("property_type", filters.property_type);
      }
      if (filters.date_type) {
        queryParams.append("date_type", filters.date_type);
      }
      if (filters.date) {
        queryParams.append("date_from", filters.date);
        queryParams.append("date", filters.date); // Also send as 'date' for compatibility
      }
      if (filters.date_to) {
        queryParams.append("date_to", filters.date_to);
      }

      const apiUrl = `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/tickets/client/cost-of-works/export/csv?${queryParams.toString()}`;
      
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("CSV export error response:", errorText);
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Create a blob from the response and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cost-of-works-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("CSV exported successfully!");
    } catch (error) {
      console.error("CSV export error:", error);
      toast.error("Failed to export CSV: " + error.message);
    }
  };

  const exportPDF = async () => {
    console.log("Exporting PDF...");
    try {
      const token = localStorage.getItem("token");
      
      // Build query string from filters (include all filters like the main query)
      const queryParams = new URLSearchParams();
      if (filters.status_id && filters.status_id !== "All") {
        queryParams.append("status_id", filters.status_id);
      }
      if (filters.property_type !== undefined) {
        queryParams.append("property_type", filters.property_type);
      }
      if (filters.date_type) {
        queryParams.append("date_type", filters.date_type);
      }
      if (filters.date) {
        queryParams.append("date_from", filters.date);
        queryParams.append("date", filters.date); // Also send as 'date' for compatibility
      }
      if (filters.date_to) {
        queryParams.append("date_to", filters.date_to);
      }

      const apiUrl = `${process.env.REACT_APP_API_URL || "http://localhost:3001"}/api/tickets/client/cost-of-works/export/pdf?${queryParams.toString()}`;
      
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("PDF export error response:", errorText);
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Create a blob from the response and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cost-of-works-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF: " + error.message);
    }
  };

  const printTable = () => {
    console.log("Printing table...");
    window.print();
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading cost data...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumb}>
        <Link to="/tickets" className={styles.breadcrumbLink}>
          Tickets
        </Link>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbCurrent}>Cost of works</span>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Title */}
        <div className={styles.titleSection}>
          <h3 className={styles.title}>Cost of works</h3>
        </div>

        {/* Panel */}
        <div className={styles.panel}>
          {/* Search Form */}
          <form onSubmit={handleSearch} className={styles.searchForm}>
            <fieldset className={styles.fieldset}>
              {/* Date Type */}
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Date</label>
                <div className={styles.inputContainer}>
                  <select
                    name="date_type"
                    value={filters.date_type}
                    onChange={(e) => handleFilterChange("date_type", e.target.value)}
                    className={styles.formControl}
                  >
                    {Object.entries(dateTypes).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Date From */}
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>From</label>
                <div className={styles.inputContainer}>
                  <div className={styles.inputGroup}>
                    <input
                      type="date"
                      name="date"
                      value={filters.date}
                      onChange={(e) => handleFilterChange("date", e.target.value)}
                      className={`${styles.formControl} ${styles.datepicker}`}
                      placeholder="dd/mm/yyyy"
                    />
                    <div className={styles.inputGroupAddon}>
                      <i className="glyphicon glyphicon-calendar"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Date To */}
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>To</label>
                <div className={styles.inputContainer}>
                  <div className={styles.inputGroup}>
                    <input
                      type="date"
                      name="date_to"
                      value={filters.date_to}
                      onChange={(e) => handleFilterChange("date_to", e.target.value)}
                      className={`${styles.formControl} ${styles.datepicker}`}
                      placeholder="dd/mm/yyyy"
                    />
                    <div className={styles.inputGroupAddon}>
                      <i className="glyphicon glyphicon-calendar"></i>
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>

            <div className={styles.filterRow}>
              {/* Status */}
              <div className={styles.formGroup}>
                <label className={styles.controlLabel}>Status</label>
                <div className={styles.inputContainer}>
                  <select
                    name="status_id"
                    value={filters.status_id}
                    onChange={(e) => handleFilterChange("status_id", e.target.value)}
                    className={styles.formControl}
                  >
                    <option value="All">All</option>
                    {statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Property Type */}
              <div className={styles.propertyTypeGroup}>
                <label className={styles.controlLabel}>Property Type</label>
                <div className={styles.radioContainer}>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="property_type"
                      value="all"
                      checked={filters.property_type === "all"}
                      onChange={(e) => handleFilterChange("property_type", e.target.value)}
                      className={styles.formRadio}
                    />
                    <span>All</span>
                  </label>
                  <label className={styles.radioLabel}>
                    <input
                      type="radio"
                      name="property_type"
                      value=""
                      checked={filters.property_type === ""}
                      onChange={(e) => handleFilterChange("property_type", e.target.value)}
                      className={styles.formRadio}
                    />
                    <span>Active only</span>
                  </label>
                </div>
              </div>

              {/* Search Button */}
              <button type="submit" className={styles.searchButton}>
                Search
              </button>
            </div>
          </form>

          {/* DataTable */}
          {totalRecords > 0 ? (
            <div className={styles.dataTableWrapper}>
              {/* DataTable Toolbar */}
              <div className={styles.dataTableToolbar}>
                {/* Length Menu */}
                <div className={styles.lengthMenu}>
                  Show{" "}
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    className={styles.lengthSelect}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={75}>75</option>
                    <option value={100}>100</option>
                  </select>{" "}
                  entries
                </div>

                {/* Export Buttons */}
                <div className={styles.exportButtons}>
                  <button onClick={exportCSV} className={styles.exportBtn}>
                    Export as CSV
                  </button>
                  <button onClick={exportPDF} className={styles.exportBtn}>
                    Export as PDF
                  </button>
                  <button onClick={printTable} className={styles.exportBtn}>
                    Print table
                  </button>
                </div>
              </div>

              {/* Table */}
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>Cost of works</th>
                    <th>No. tickets</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, index) => (
                    <tr key={index}>
                      <td>{row.property_name}</td>
                      <td>{row.cost_of_works}</td>
                      <td>{row.ticketsNumber}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th></th>
                    <th>
                      £
                      {tableData
                        .reduce((sum, row) => sum + (row.cost_of_works_number || 0), 0)
                        .toFixed(2)}
                    </th>
                    <th>
                      {tableData.reduce((sum, row) => sum + (row.ticketsNumber || 0), 0)}
                    </th>
                  </tr>
                </tfoot>
              </table>

              {/* DataTable Info and Pagination */}
              <div className={styles.dataTableFooter}>
                <div className={styles.dataTableInfo}>
                  Showing {((currentPage - 1) * pageSize) + 1} to{" "}
                  {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} entries
                </div>
                
                <div className={styles.dataTablePaginate}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`${styles.pageBtn} ${currentPage === 1 ? styles.disabled : ""}`}
                  >
                    Previous
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`${styles.pageBtn} ${currentPage === page ? styles.current : ""}`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`${styles.pageBtn} ${currentPage === totalPages ? styles.disabled : ""}`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.noResults}>
              {error ? `Error: ${error}` : "No tickets!"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CostOfWorks;
